import { createContext, useContext, useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';
import api from '../api';
import { safeStorage } from '../utils/safeStorage';
import { logger } from '../utils/logger';

/**
 * Timezone Context
 * ------------------------------------------------------------------
 * Precedence for the active zone:
 *   1. Server-persisted `user.timezone` (if authenticated)
 *   2. localStorage override (for logged-out browsing / pre-auth)
 *   3. Browser auto-detected zone
 *
 * On first authentication — if the server has no value but the browser
 * does — we seed the server once via `PUT /api/auth/profile`. On user
 * override (Settings picker) we persist to BOTH localStorage and server.
 *
 * What it exposes
 *   - `timezone`               IANA zone string, e.g. "Asia/Kolkata"
 *   - `setTimezone(zone)`      Override (persisted to localStorage + server)
 *   - `resetTimezone()`        Clear override → re-detect from browser
 *   - `isOverridden`           Boolean: user set this explicitly
 *   - `browserTimezone`        The detected browser zone
 *   - `formatInTz(date, opts)` Render any Date/ISO in the chosen zone
 *   - `nowInTz()`              `new Date()` normalized to the chosen zone (wall time)
 *   - `todayDateStrInTz()`     YYYY-MM-DD as of "today" in the chosen zone
 *   - `zoneOffsetLabel()`      e.g. "GMT+5:30" for the chosen zone
 *
 * What it deliberately does NOT do
 *   - Convert existing stored block "HH:MM" strings into UTC Instants.
 *     PolkDesQ's calendar currently treats those as local wall-clock
 *     strings; a full TZ migration would require the backend to store
 *     absolute Instants.  This prototype only makes the *display* layer
 *     timezone-aware so you can A/B the UX before committing to a
 *     schema migration.
 */

const STORAGE_KEY = 'polkdesq_timezone_override';

const TimezoneContext = createContext(null);

function detectBrowserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

export function TimezoneProvider({ children }) {
  const { user, updateUser } = useAuth();
  const browserTimezone = useMemo(() => detectBrowserTimezone(), []);
  const [override, setOverride] = useState(() => {
    try { return safeStorage.getString(STORAGE_KEY) || null; } catch { return null; }
  });
  const seededRef = useRef(false);

  // Server value wins over localStorage override, which wins over browser.
  const serverZone = user?.timezone && user.timezone !== 'UTC' ? user.timezone : null;
  const timezone = serverZone || override || browserTimezone;
  const isOverridden = !!(serverZone || override);

  // One-time seed: authenticated user with no server timezone → push browser zone.
  useEffect(() => {
    if (!user || seededRef.current) return;
    if (!user.timezone || user.timezone === 'UTC') {
      if (browserTimezone && browserTimezone !== 'UTC') {
        seededRef.current = true;
        api.put('/auth/profile', { timezone: browserTimezone })
          .then(() => updateUser({ timezone: browserTimezone }))
          .catch((err) => { logger.error('Failed to seed timezone:', err); seededRef.current = false; });
      } else {
        seededRef.current = true;
      }
    } else {
      seededRef.current = true;
    }
  }, [user, browserTimezone, updateUser]);

  const setTimezone = useCallback((zone) => {
    if (!zone) return;
    setOverride(zone);
    try { safeStorage.setString(STORAGE_KEY, zone); } catch (err) { logger.warn('Timezone override not persisted (storage disabled):', err?.message); }
    // Persist server-side too (fire-and-forget — UI updates optimistically).
    if (user) {
      api.put('/auth/profile', { timezone: zone })
        .then(() => updateUser({ timezone: zone }))
        .catch((err) => logger.error('Failed to persist timezone:', err));
    }
  }, [user, updateUser]);

  const resetTimezone = useCallback(() => {
    setOverride(null);
    try { safeStorage.remove(STORAGE_KEY); } catch (err) { logger.warn('Timezone override not cleared (storage disabled):', err?.message); }
    if (user) {
      api.put('/auth/profile', { timezone: browserTimezone })
        .then(() => updateUser({ timezone: browserTimezone }))
        .catch((err) => logger.error('Failed to reset timezone:', err));
    }
  }, [user, updateUser, browserTimezone]);

  const value = useMemo(() => ({
    timezone,
    browserTimezone,
    isOverridden,
    setTimezone,
    resetTimezone,

    formatInTz: (date, opts = {}) => {
      if (!date) return '';
      const d = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
      if (Number.isNaN(d.getTime())) return '';
      return new Intl.DateTimeFormat(undefined, { timeZone: timezone, ...opts }).format(d);
    },

    nowInTz: () => {
      // Returns a *string-free* wall-clock Date in the chosen zone by using
      // the trick of parts-extraction.  Preserves the shape of a Date so
      // existing consumers (`getHours()` etc.) keep working.
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
      }).formatToParts(new Date()).reduce((acc, p) => { acc[p.type] = p.value; return acc; }, {});
      return new Date(
        Number(parts.year),
        Number(parts.month) - 1,
        Number(parts.day),
        parts.hour === '24' ? 0 : Number(parts.hour),
        Number(parts.minute),
        Number(parts.second),
      );
    },

    todayDateStrInTz: () => {
      const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit',
      }).formatToParts(new Date()).reduce((acc, p) => { acc[p.type] = p.value; return acc; }, {});
      return `${parts.year}-${parts.month}-${parts.day}`;
    },

    zoneOffsetLabel: () => {
      try {
        const parts = new Intl.DateTimeFormat('en-US', { timeZone: timezone, timeZoneName: 'shortOffset' })
          .formatToParts(new Date());
        const off = parts.find((p) => p.type === 'timeZoneName')?.value;
        return off || '';
      } catch { return ''; }
    },
  }), [timezone, browserTimezone, isOverridden, setTimezone, resetTimezone]);

  return <TimezoneContext.Provider value={value}>{children}</TimezoneContext.Provider>;
}

/**
 * Hook accessor.  Safe to call even outside the provider — falls back to
 * browser-detected timezone with no-op setters.
 */
export function useTimezone() {
  const ctx = useContext(TimezoneContext);
  if (ctx) return ctx;
  // Safe fallback for components rendered above the provider or in tests.
  const browserTimezone = detectBrowserTimezone();
  return {
    timezone: browserTimezone,
    browserTimezone,
    isOverridden: false,
    setTimezone: () => {},
    resetTimezone: () => {},
    formatInTz: (d, o) => (d ? new Intl.DateTimeFormat(undefined, { timeZone: browserTimezone, ...o }).format(new Date(d)) : ''),
    nowInTz: () => new Date(),
    // Same tz-correct implementation as the provider (DATETIME-IST-TODAY-SWEEP-1):
    // a UTC slice here returned "yesterday" for 00:00–05:30 IST every night.
    todayDateStrInTz: () => new Intl.DateTimeFormat('en-CA', {
      timeZone: browserTimezone, year: 'numeric', month: '2-digit', day: '2-digit',
    }).format(new Date()),
    zoneOffsetLabel: () => '',
  };
}
